// App-getriebener Sync der offline erfassten Check-ins (kein Background-Sync — funktioniert auch auf iOS).
// GENAU EIN Replay-Aktor: Modul-Mutex `draining` verhindert überlappende Läufe.
// Trigger (nur im Online-Modus): App-Start (init), Offline-Modus verlassen (reconnect), App-Vordergrund
// (visibilitychange) und nach erfolgreichem Login (401 unterbricht den Sync, siehe drain()).

import api from '../components/wrapper/API';
import * as db from './db';
import { isOffline, subscribeConnectivity } from './connectivity';

let draining = false;
const syncListeners = new Set();

// Nach einem Drain benachrichtigt: betroffene Event-IDs (UI kann neu laden / Badges leeren).
export function subscribeSync(cb) {
  syncListeners.add(cb);
  return () => { syncListeners.delete(cb); };
}
function emitSync(eventIds) {
  for (const cb of syncListeners) {
    try { cb(eventIds); } catch (e) { /* ignore */ }
  }
}

const epUrl = (eventId) => `/EventPlayer?eventId=${eventId}`;
const evUrl = (eventId) => `/Event/GetById?eventId=${eventId}`;

// Verarbeitet die Präsenz-Queue: älteste Absicht zuerst (clientTimestamp aufsteigend).
//   2xx -> Zeile löschen · 404 -> droppen (Spieler weg) · 401 -> anhalten OHNE Queue zu verwerfen
//   5xx -> pending lassen + Pass abbrechen (Server-Problem, später erneut) · sonstige 4xx -> failed
//   Netzwerkfehler/Timeout -> Pass abbrechen (Interceptor hat in den Offline-Modus geschaltet).
export async function drain() {
  if (draining || !db.isSupported() || isOffline()) return;
  draining = true;
  const syncedEventIds = new Set();
  try {
    const rows = (await db.getAllQueue()) || [];
    rows.sort((a, b) => String(a.clientTimestamp).localeCompare(String(b.clientTimestamp)));

    for (const row of rows) {
      if (row.state === 'failed') continue; // dauerhaft fehlerhafte Zeile nicht endlos retryen
      try {
        await api.put(`/EventPlayer/${row.eventPlayerId}/Presence`, {
          presenceStatus: row.presenceStatus,
          clientTimestamp: row.clientTimestamp,
        });
        await db.deleteQueueItem(row.id);
        syncedEventIds.add(row.eventId);
      } catch (err) {
        if (!err || !err.response) break;
        const status = err.response.status;
        if (status === 401) break; // Session abgelaufen -> Re-Login (Interceptor), danach drain()
        if (status === 404) {
          await db.deleteQueueItem(row.id); // Spieler zwischenzeitlich entfernt -> Zeile droppen
          syncedEventIds.add(row.eventId);
          continue;
        }
        // Zeile nur aktualisieren, wenn sie noch existiert (ein neuerer Tap kann sie ersetzt haben).
        if (!(await db.getQueueItem(row.id))) continue;
        const attempts = (row.attempts ?? 0) + 1;
        if (status >= 500) {
          await db.putQueueItem({ ...row, attempts, lastError: String(status) });
          break;
        }
        await db.putQueueItem({
          ...row,
          state: 'failed',
          attempts,
          lastError: String(err.response.data ?? status ?? 'Fehler'),
        });
      }
    }
  } catch (e) {
    /* IndexedDB-Fehler: beim nächsten Trigger erneut */
  } finally {
    draining = false;
  }

  if (syncedEventIds.size > 0) emitSync(Array.from(syncedEventIds));
  await cleanupExpiredCaches();
}

// Mitternacht NACH dem Spieltag (= Beginn des Folgetags) erreicht?
function isPastEventDay(eventDate) {
  if (!eventDate) return false;
  const d = new Date(eventDate);
  if (isNaN(d.getTime())) return false;
  const endOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);
  return new Date() >= endOfDay;
}

// Auto-Löschen NUR wenn der Spieltag-Tag vorbei ist UND keine Check-ins mehr ausstehen.
// Steht noch etwas zum Übertragen in der Queue, bleibt die komplette Kopie erhalten (kein Datenverlust).
// Vom Server endgültig abgelehnte Zeilen ('failed') werden nie mehr übertragen -> nach dem Spieltag
// verwerfen, sonst hielten sie die Kopie (personenbezogene Daten) für immer fest.
export async function cleanupExpiredCaches() {
  if (!db.isSupported()) return;
  try {
    const metas = (await db.getAllMeta()) || [];
    for (const m of metas) {
      if (!m.key || m.key.indexOf('event:') !== 0) continue;
      if (!isPastEventDay(m.eventDate)) continue;
      const rows = (await db.getQueueByEvent(m.eventId)) || [];
      for (const r of rows) {
        if (r.state === 'failed') await db.deleteQueueItem(r.id);
      }
      if (rows.some(r => r.state !== 'failed')) continue; // noch nicht übertragen -> Kopie behalten
      await db.deleteRead(epUrl(m.eventId));
      await db.deleteRead(evUrl(m.eventId));
      await db.deleteMeta(m.key);
    }
  } catch (e) { /* best effort */ }
}

let initialized = false;
export function init() {
  if (initialized) return;
  initialized = true;

  // Offline-Modus wurde bewusst verlassen (reconnect) -> übertragen.
  subscribeConnectivity((offline) => { if (!offline) drain(); });

  // App-Vordergrund -> übertragen (liest nur IndexedDB; Netz nur bei offenen Einträgen).
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') drain();
    });
  }

  // Start: persistenten Speicher anfordern + aufräumen + übertragen (drain prüft den Modus selbst).
  db.requestPersistentStorage();
  cleanupExpiredCaches();
  drain();
}
